// Anomalies (H4-11) : constats des règles (tâche quotidienne ou détection à la demande) et des
// actes (notification tardive ou contradictoire, trop perçu, écarts) ; liste filtrée par état et
// type, instruction par un responsable, conclusion motivée (expliquée ou confirmée). Aucune
// conséquence automatique : une anomalie signale un point à vérifier, elle ne prononce rien.
import { Bandeau, EtatVide, Filtres, Lien, LienBouton, Panneau, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Search, ShieldAlert } from 'lucide-react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { CODES_TYPE_ANOMALIE, LIBELLES_ETAT_ANOMALIE, LIBELLES_TYPE_ANOMALIE, hrefObjetAnomalie, libelle, tonAnomalie, type Anomalie } from './donnees';
import { FormulaireAction } from './FormulaireAction';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { useAnomalie, useAnomalies } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

function puce(a: Pick<Anomalie, 'etat'>) {
  return <PuceEtat ton={tonAnomalie(a.etat)} libelle={libelle(LIBELLES_ETAT_ANOMALIE, a.etat)} />;
}

function LienObjet({ objet }: { objet: Anomalie['objet'] }) {
  const href = hrefObjetAnomalie(objet);
  const texte = `${objet.type} ${objet.id.slice(0, 8)}`;
  return href ? <Lien href={href}>{texte}</Lien> : <span>{texte}</span>;
}

/** Détail d'une anomalie en texte lisible (clé : valeur), sans inventer de libellés. */
export function texteDetail(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .map(([k, v]) => `${k} : ${typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}`)
    .join(' ; ');
}

export function PageAnomalie() {
  const { id = '' } = useParams();
  const droits = useDroitsRecettes();
  const fiche = useAnomalie(id);
  const a = fiche.data;
  const ouverte = a ? a.etat === 'ouverte' || a.etat === 'en_instruction' : false;
  return (
    <>
      <EnTetePage etiquette="Finances, anomalie" titre={a ? a.libelle : 'Anomalie'} description={a ? <span className={commun.ligne}>{puce(a)}</span> : null} actions={<LienBouton href="/recettes/anomalies" icone={ArrowLeft}>Anomalies</LienBouton>} />
      <NavigationFinances />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L2-A" fonction="La fiche de l'anomalie">
        {a ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              <Panneau titre="Constat">
                <dl className={commun.definitions}>
                  <dt>Type</dt>
                  <dd>{libelle(LIBELLES_TYPE_ANOMALIE, a.type)}</dd>
                  <dt>Objet</dt>
                  <dd><LienObjet objet={a.objet} /></dd>
                  <dt>Ouverte le</dt>
                  <dd>{dateHeure(a.cree_le)}</dd>
                  <dt>Détail</dt>
                  <dd>{Object.keys(a.detail ?? {}).length > 0 ? texteDetail(a.detail) : 'aucun détail'}</dd>
                  {a.conclusion ? (
                    <>
                      <dt>Conclusion</dt>
                      <dd>{a.conclusion} ({dateHeure(a.conclue_le)})</dd>
                    </>
                  ) : null}
                </dl>
              </Panneau>
              <Panneau titre="Historique">
                <ol className={styles.liste}>
                  {a.historique.map((h, i) => (
                    <li key={i}>
                      <span>
                        <strong>{libelle(LIBELLES_ETAT_ANOMALIE, h.etat_apres)}</strong>
                        <span className={styles.sous}>{dateHeure(h.horodatage)}{h.commentaire ? ` ; ${h.commentaire}` : ''}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </Panneau>
            </div>
            <Panneau titre="Instruction">
              {!droits.modifier ? <p className={commun.na}>L&apos;instruction et la conclusion reviennent au financier.</p> : null}
              <div className={styles.actions}>
                {ouverte && droits.modifier ? <FormulaireAction libelle="Instruire" titre="Prendre l'anomalie en instruction" explication="Vous devenez responsable de l'instruction ; le commentaire est conservé dans l'historique." chemin={`/recettes/anomalies/${a.id}/instruire`} champs={[{ nom: 'commentaire', libelle: 'Commentaire', type: 'multiligne' }]} succes="Anomalie en instruction." variante="secondaire" /> : null}
                {ouverte && droits.modifier ? (
                  <FormulaireAction
                    libelle="Conclure"
                    titre="Conclure l'anomalie"
                    explication="Expliquée (le constat a une explication régulière) ou confirmée (le constat est avéré). La conclusion est motivée ; aucune conséquence automatique n'en découle."
                    chemin={`/recettes/anomalies/${a.id}/conclure`}
                    champs={[
                      { nom: 'etat', libelle: 'Conclusion', type: 'selection', obligatoire: true, options: [{ valeur: 'expliquee', libelle: 'Expliquée' }, { valeur: 'confirmee', libelle: 'Confirmée' }] },
                      { nom: 'conclusion', libelle: 'Motivation', type: 'multiligne', obligatoire: true },
                    ]}
                    succes="Anomalie conclue."
                  />
                ) : null}
              </div>
              {!ouverte ? <Bandeau type="info">Anomalie conclue.</Bandeau> : null}
            </Panneau>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}

export function PageAnomalies() {
  const droits = useDroitsRecettes();
  const [filtres, setFiltres] = useFiltresPersistes('recettes-anomalies', { ouvertes: 'oui', etat: '', type: '' });
  const liste = useAnomalies({ ouvertes: filtres.etat ? '' : (filtres.ouvertes ?? ''), etat: filtres.etat ?? '', type: filtres.type ?? '' });
  const colonnes: ColonneTableau<Anomalie>[] = [
    { id: 'libelle', entete: 'Anomalie', enTeteLigne: true, masquable: false, valeurTri: (a) => a.libelle, cellule: (a) => <Lien href={`/recettes/anomalies/${a.id}`}>{a.libelle}</Lien> },
    { id: 'objet', entete: 'Objet', cellule: (a) => <LienObjet objet={a.objet} /> },
    { id: 'detail', entete: 'Détail', cellule: (a) => <span className={styles.sous}>{Object.keys(a.detail ?? {}).length > 0 ? texteDetail(a.detail) : 'aucun détail'}</span> },
    { id: 'cree', entete: 'Ouverte le', valeurTri: (a) => a.cree_le, cellule: (a) => dateHeure(a.cree_le) },
    { id: 'etat', entete: 'État', valeurTri: (a) => a.etat, cellule: puce },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Anomalies"
        description="Constats du dispositif de contrôle : paiements non confirmés depuis 48 heures, annulations répétées, doublons, tarifs sans acte, remises excessives, écarts de caisse ou de rapprochement, notifications tardives ou contradictoires, trop perçus."
        actions={droits.modifier ? <FormulaireAction<{ nouvelles: number }> libelle="Détecter maintenant" titre="Exécuter les règles d'anomalies" explication="Les règles quotidiennes sont exécutées maintenant pour la collectivité ; une anomalie déjà connue n'est pas ouverte deux fois." chemin="/recettes/anomalies/detecter" icone={Search} succes={(r) => `Détection terminée : ${r.nouvelles} nouvelle(s) anomalie(s).`} variante="secondaire" /> : null}
      />
      <NavigationFinances />
      <Filtres
        libelle="Filtres des anomalies"
        groupes={[
          { id: 'ouvertes', libelle: 'Affichage', options: [{ valeur: 'oui', libelle: 'À instruire' }, { valeur: '', libelle: 'Toutes' }] },
          { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...Object.entries(LIBELLES_ETAT_ANOMALIE).map(([valeur, l]) => ({ valeur, libelle: l }))] },
          { id: 'type', libelle: 'Type', options: [{ valeur: '', libelle: 'Tous' }, ...CODES_TYPE_ANOMALIE.map((t) => ({ valeur: t, libelle: LIBELLES_TYPE_ANOMALIE[t] ?? t }))] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-A" fonction="La liste des anomalies">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune anomalie" icone={ShieldAlert}>
            <p>Aucune anomalie ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Anomalies" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(a) => a.id} />
        )}
      </EtatRequete>
      <p className={styles.note}>Une anomalie signale un point à vérifier ; elle ne prononce rien.</p>
    </>
  );
}
